import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ProductService } from '../../../services/product.service';
import { Product, DoseType } from '../../../interfaces/product.interface';
import { ToastService } from '../../../services/toast.service';
import { FileUploadService, UploadResult, UploadProgress } from '../../../services/file-upload.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-product-management',
  templateUrl: './product-management.component.html',
  styleUrls: ['./product-management.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class ProductManagementComponent implements OnInit, OnDestroy {
  products: Product[] = [];
  filteredProducts: Product[] = [];
  loading = false;
  error: string | null = null;
  
  // Form properties
  productForm!: FormGroup;
  multilingualForm!: FormGroup;
  isEditing = false;
  editingProductId: string | null = null;
  
  // Search and filter properties
  searchTerm = '';
  selectedDoseType: DoseType | 'all' = 'all';
  selectedStatus: 'all' | 'active' | 'inactive' = 'all';
  
  // Modal properties
  selectedProduct: Product | null = null;
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  
  // File upload properties
  selectedFiles: File[] = [];
  imagePreviewUrls: string[] = [];
  defaultImageUrl = 'https://placehold.co/400x400/66BB6A/31343C?font=poppins&text=Dose+One';
  isUploading = false;
  uploadProgress = 0;
  uploadProgressDetails: { [key: number]: UploadProgress } = {}; // Progress for each file
  totalUploadProgress = 0; // Overall progress
  
  // Image deletion properties
  deletingImageIndex: number | null = null;
  isDeletingImage = false;
  isClearingAllImages = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    private productService: ProductService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private fileUploadService: FileUploadService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
    this.initMultilingualForm();
  }

  private initForm(): void {
    // Calculate next available dose number
    const nextDoseNumber = this.getNextAvailableDoseNumber();
    
    this.productForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      doseType: ['', Validators.required],
      doseNumber: [nextDoseNumber, [Validators.required, Validators.min(1)]],
      features: ['', Validators.required],
      benefits: [''],
      ingredients: [''],
      usage: [''],
      inStock: [true],
      isActive: [true],
      badge: [''],
      i18n: [null],
      twoDosesPrice: [0, [Validators.min(0)]],
      threeDosesPrice: [0, [Validators.min(0)]],
      image: [[], [this.imageArrayValidator.bind(this)]]
    });
  }

  private createFreshForm(): void {
    const nextDoseNumber = this.getNextAvailableDoseNumber();
    
    this.productForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      doseType: ['', Validators.required],
      doseNumber: [nextDoseNumber, [Validators.required, Validators.min(1)]],
      features: ['', Validators.required],
      benefits: [''],
      ingredients: [''],
      usage: [''],
      inStock: [true],
      isActive: [true],
      badge: [''],
      i18n: [null],
      twoDosesPrice: [0, [Validators.min(0)]],
      threeDosesPrice: [0, [Validators.min(0)]],
      image: [[], [this.imageArrayValidator.bind(this)]]
    });
    
    this.resetUploadState();
    
    // Trigger change detection to ensure form is properly bound
    this.cdr.detectChanges();
  }

  private initMultilingualForm(): void {
    this.multilingualForm = this.formBuilder.group({
      // Hindi fields
      hi_name: [''],
      hi_description: [''],
      hi_features: [''],
      hi_benefits: [''],
      hi_usage: [''],
      // Marathi fields
      mr_name: [''],
      mr_description: [''],
      mr_features: [''],
      mr_benefits: [''],
      mr_usage: [''],
      // Kannada fields
      kn_name: [''],
      kn_description: [''],
      kn_features: [''],
      kn_benefits: [''],
      kn_usage: ['']
    });
  }

  /**
   * Calculate the next available dose number for new products
   * @returns The next available dose number
   */
  private getNextAvailableDoseNumber(): number {
    if (!this.products || this.products.length === 0) {
      return 1;
    }
    
    // Get all existing dose numbers and sort them
    const existingDoseNumbers = this.products
      .map(p => p.doseNumber)
      .filter(num => num !== null && num !== undefined)
      .sort((a, b) => a - b);
    
    if (existingDoseNumbers.length === 0) {
      return 1;
    }
    
    // Find the first gap in the sequence, or use the next number after the highest
    let nextNumber = 1;
    for (const num of existingDoseNumbers) {
      if (num > nextNumber) {
        break; // Found a gap
      }
      nextNumber = num + 1;
    }
    
    return nextNumber;
  }

  /**
   * Get the next sequential product ID
   * @returns The next available product ID
   */
  getNextSequentialProductId(): string {
    if (!this.products || this.products.length === 0) {
      return '1';
    }
    
    // Find the highest numeric ID
    const numericIds = this.products
      .map(p => p.id)
      .filter(id => !isNaN(Number(id)))
      .map(id => Number(id));
    
    if (numericIds.length === 0) {
      return '1'; // No numeric IDs found
    }
    
    const maxId = Math.max(...numericIds);
    return (maxId + 1).toString();
  }

  /**
   * Get current product sequence for reference
   * @returns Array of current dose numbers and their products
   */
  getCurrentProductSequence(): Array<{doseNumber: number, name: string, doseType: string}> {
    if (!this.products || this.products.length === 0) {
      return [];
    }
    
    return this.products
      .filter(p => p.doseNumber !== null && p.doseNumber !== undefined)
      .sort((a, b) => a.doseNumber - b.doseNumber)
      .map(p => ({
        doseNumber: p.doseNumber,
        name: p.name,
        doseType: p.doseType
      }));
  }

  /**
   * Check if a dose number is already in use
   * @param doseNumber The dose number to check
   * @param excludeProductId Product ID to exclude from check (for editing)
   * @returns True if dose number is already used
   */
  isDoseNumberInUse(doseNumber: number, excludeProductId?: string): boolean {
    if (!this.products || this.products.length === 0) {
      return false;
    }
    
    return this.products.some(p => 
      p.doseNumber === doseNumber && 
      p.id !== excludeProductId
    );
  }

  /**
   * Get suggested alternative dose numbers
   * @param currentDoseNumber Current dose number being used
   * @param excludeProductId Product ID to exclude from suggestions
   * @returns Array of suggested alternative dose numbers
   */
  getSuggestedDoseNumbers(currentDoseNumber: number, excludeProductId?: string): number[] {
    if (!this.products || this.products.length === 0) {
      return [1, 2, 3];
    }
    
    // Get all existing dose numbers
    const existingDoseNumbers = this.products
      .filter(p => p.id !== excludeProductId)
      .map(p => p.doseNumber)
      .filter(num => num !== null && num !== undefined);
    
    // Find gaps in the sequence
    const suggestions: number[] = [];
    let num = 1;
    const maxExisting = Math.max(...existingDoseNumbers, 0);
    
    // Suggest numbers up to max + 3
    while (num <= maxExisting + 3 && suggestions.length < 5) {
      if (!existingDoseNumbers.includes(num)) {
        suggestions.push(num);
      }
      num++;
    }
    
    return suggestions;
  }

  /**
   * Validate dose number and show warnings if needed
   * @param doseNumber The dose number to validate
   * @param excludeProductId Product ID to exclude from validation
   */
  validateDoseNumber(doseNumber: number, excludeProductId?: string): { isValid: boolean; warning?: string; suggestions?: number[] } {
    if (doseNumber < 1) {
      return { isValid: false, warning: 'Dose number must be at least 1' };
    }
    
    if (this.isDoseNumberInUse(doseNumber, excludeProductId)) {
      const suggestions = this.getSuggestedDoseNumbers(doseNumber, excludeProductId);
      return {
        isValid: false,
        warning: `Dose number ${doseNumber} is already in use. Suggested alternatives: ${suggestions.join(', ')}`,
        suggestions
      };
    }
    
    return { isValid: true };
  }

  /**
   * Reset all upload-related state variables
   */
  private resetUploadState(): void {
    this.isUploading = false;
    this.uploadProgress = 0;
    this.totalUploadProgress = 0;
    this.uploadProgressDetails = {};
    this.selectedFiles = [];
    this.imagePreviewUrls = [];
  }

  ngOnInit(): void {
    // Ensure form is initialized
    if (!this.productForm) {
      this.initForm();
    }
    this.checkFirebaseStorage();
    this.loadProducts();
    this.checkProductFolder();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadProducts(): Promise<void> {
    this.loading = true;
    this.error = null;
    
    try {
      this.productService.getProducts()
        .pipe(takeUntil(this.destroy$))
        .subscribe(products => {
          console.log(products)
          this.products = products;
          this.filteredProducts = products;
          this.loading = false;
        });
    } catch (error) {
      this.error = 'Failed to load products';
      this.loading = false;
      console.error('Error loading products:', error);
    }
  }

  // Search and filter methods
  filterProducts(): void {
    let filtered = this.products;

    // Search by name or description
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower)
      );
    }

    // Filter by dose type
    if (this.selectedDoseType !== 'all') {
      filtered = filtered.filter(product => product.doseType === this.selectedDoseType);
    }

    // Filter by status
    if (this.selectedStatus !== 'all') {
      const isActive = this.selectedStatus === 'active';
      filtered = filtered.filter(product => product.isActive === isActive);
    }

    this.filteredProducts = filtered;
    this.currentPage = 1; // Reset to first page when filtering
  }

  // CRUD Operations
  openAddModal(content: any): void {
    this.isEditing = false;
    this.editingProductId = null;
    this.selectedProduct = null;
    
    // Reset upload state
    this.resetUploadState();
    
    // Create a fresh form instance
    this.createFreshForm();
    
    // Open modal with close handler
    const modalRef = this.modalService.open(content, { size: 'xl', scrollable: true });
    
    // Reset state when modal is closed
    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetUploadState();
    });
  }

  openEditModal(content: any, product: Product): void {
    this.isEditing = true;
    this.editingProductId = product.id;
    this.selectedProduct = product;
    
    // Reset upload state
    this.resetUploadState();
    
    // Create a fresh form instance and populate it
    this.createFreshForm();
    this.populateForm(product);
    
    // Clear file uploads for edit mode
    this.selectedFiles = [];
    this.imagePreviewUrls = [];
    
    // Open modal with close handler
    const modalRef = this.modalService.open(content, { size: 'xl', scrollable: true });
    
    // Reset state when modal is closed
    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetUploadState();
    });
  }

  openDeleteModal(content: any, product: Product): void {
    this.selectedProduct = product;
    this.modalService.open(content, { size: 'sm' });
  }

  async saveProduct(): Promise<void> {
    if (this.productForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    // Validate dose number before saving
    const doseNumber = this.productForm.get('doseNumber')?.value;
    const doseValidation = this.validateDoseNumber(doseNumber, this.editingProductId || undefined);
    
    if (!doseValidation.isValid) {
      this.toastService.warning(doseValidation.warning || 'Invalid dose number');
      return;
    }

    try {
      const formValue = this.productForm.value;
      
      // Handle file uploads first
      let imagePaths: string[] = [];
      
      if (this.selectedFiles.length > 0) {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.totalUploadProgress = 0;
        this.uploadProgressDetails = {};
        this.toastService.info('Starting image upload...');
        

        
        // Use single upload with progress tracking
        this.fileUploadService.uploadMultipleFilesWithProgress(this.selectedFiles)
          .subscribe({
            next: (data) => {
              const { result, progress, fileIndex } = data;
              
              // Update progress for specific file
              this.uploadProgressDetails[fileIndex] = progress;
              
              // Calculate overall progress
              this.calculateTotalProgress();
              
              // Update UI
              this.cdr.detectChanges();
              
              // Check if this is the final result (upload complete)
              if (result && result.success) {
                // This is a completed upload result
                const uploadResults = Array.isArray(result) ? result : [result];
                const successfulUploads = uploadResults.filter((uploadResult: any) => uploadResult.success);
                imagePaths = successfulUploads.map((uploadResult: any) => uploadResult.downloadURL || uploadResult.filePath!);
                
                console.log('Firebase upload results:', {
                  totalFiles: this.selectedFiles.length,
                  successfulUploads: successfulUploads.length,
                  imagePaths: imagePaths
                });
                
                if (successfulUploads.length !== uploadResults.length) {
                  this.toastService.error('Some images failed to upload. Please try again.');
                  return;
                }
                
                if (successfulUploads.length > 0) {
                  this.toastService.success(`Successfully uploaded ${successfulUploads.length} image(s) to Firebase!`);
                }
                
                // Continue with product creation/update
                console.log('About to call completeProductSave with imagePaths:', imagePaths);
                this.completeProductSave(imagePaths).then(() => {
                  console.log('completeProductSave completed successfully');
                }).catch((error: any) => {
                  console.error('Error in completeProductSave:', error);
                });
              }
            },
            error: (error) => {
              console.error('Upload error:', error);
              this.toastService.error('Upload failed. Please try again.');
              this.resetUploadState();
            }
          });
        
        // Note: The upload will continue asynchronously
        // The completeProductSave will be called from the complete callback
        return;
      }

      // If no files to upload, complete the save immediately
      if (this.selectedFiles.length === 0) {
        await this.completeProductSave([]);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      this.toastService.error('Failed to save product. Please try again.');
      // Reset loading state on error
      this.resetUploadState();
    }
  }

  async createProduct(productData: Partial<Product>): Promise<string> {
    try {
      return await this.productService.createProduct(productData as Omit<Product, 'id'>);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  async updateProduct(productId: string, productData: Partial<Product>): Promise<void> {
    try {
      await this.productService.updateProduct(productId, productData);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /**
   * Calculate total upload progress across all files
   */
  private calculateTotalProgress(): void {
    if (Object.keys(this.uploadProgressDetails).length === 0) {
      this.totalUploadProgress = 0;
      return;
    }

    const totalPercentage = Object.values(this.uploadProgressDetails)
      .reduce((sum, progress) => sum + progress.percentage, 0);
    
    this.totalUploadProgress = Math.round(totalPercentage / Object.keys(this.uploadProgressDetails).length);
    this.uploadProgress = this.totalUploadProgress;
    
    // Force change detection
    this.cdr.detectChanges();
  }



  /**
   * Complete product save after uploads are done
   */
  private async completeProductSave(imagePaths: string[]): Promise<void> {
    console.log('completeProductSave method called with imagePaths:', imagePaths);
    try {
      const formValue = this.productForm.value;
      
      // Combine existing images with new uploaded images
      const existingImages = formValue.image || [];
      const allImages = [...existingImages, ...imagePaths];
      
      const productData: Partial<Product> = {
        ...formValue,
        image: allImages,
        features: this.parseArrayFromString(formValue.features),
        benefits: this.parseArrayFromString(formValue.benefits),
        ingredients: this.parseArrayFromString(formValue.ingredients),
        usage: this.parseArrayFromString(formValue.usage),
        doseNumber: Number(formValue.doseNumber),
        comboPricing: {
          twoDoses: Number(formValue.twoDosesPrice || 0),
          threeDoses: Number(formValue.threeDosesPrice || 0)
        }
      };

      if (formValue.i18n && Object.keys(formValue.i18n).length > 0) {
        productData.i18n = formValue.i18n;
      }

      if (this.isEditing && this.editingProductId) {
        await this.updateProduct(this.editingProductId, productData);
        this.toastService.success('Product updated successfully!');
      } else {
        await this.createProduct(productData);
        this.toastService.success('Product created successfully!');
      }

      // Close all modals after successful operation
      console.log('Attempting to close modal...');
      
      try {
        // Try to close all modals
        this.modalService.dismissAll();
        console.log('Modal dismissAll() called successfully');
        
        // Force change detection
        this.cdr.detectChanges();
        
        // Try again after a short delay to ensure it's closed
        setTimeout(() => {
          try {
            console.log('Attempting to close modal again after delay...');
            this.modalService.dismissAll();
            console.log('Modal dismissAll() called again after delay');
            this.cdr.detectChanges();
          } catch (error) {
            console.error('Error calling dismissAll() after delay:', error);
          }
        }, 300);
        
      } catch (error) {
        console.error('Error calling dismissAll():', error);
      }
      
      // Reset upload state after successful save
      this.resetUploadState();
      
      this.resetForm();
      this.loadProducts();
    } catch (error) {
      console.error('Error completing product save:', error);
      this.toastService.error('Failed to save product. Please try again.');
      
      // Reset upload state on error as well
      this.resetUploadState();
    }
  }

  async deleteProduct(productId: string): Promise<void> {
    try {
      await this.productService.deleteProduct(productId);
      this.toastService.success('Product deleted successfully!');
      this.modalService.dismissAll();
    } catch (error) {
      console.error('Error deleting product:', error);
      this.toastService.error('Failed to delete product. Please try again.');
    }
  }

  // Form helper methods
  private populateForm(product: Product): void {
    this.productForm.reset();
    
    this.productForm.patchValue({
      name: product.name || '',
      description: product.description || '',
      doseType: product.doseType || '',
      doseNumber: product.doseNumber || 1,
      features: this.arrayToString(product.features),
      benefits: this.arrayToString(product.benefits),
      ingredients: this.arrayToString(product.ingredients),
      usage: this.arrayToString(product.usage),
      inStock: product.inStock !== undefined ? product.inStock : true,
      isActive: product.isActive !== undefined ? product.isActive : true,
      badge: product.badge || '',
      i18n: product.i18n || null,
      image: this.getImageArray(product.image),
      twoDosesPrice: product.comboPricing?.twoDoses || 0,
      threeDosesPrice: product.comboPricing?.threeDoses || 0
    });
  }

  private resetForm(): void {
    // Reset the entire form
    this.productForm.reset();
    
    // Calculate next available dose number
    const nextDoseNumber = this.getNextAvailableDoseNumber();
    
    // Set default values
    this.productForm.patchValue({
      doseType: '',
      doseNumber: nextDoseNumber,
      inStock: true,
      isActive: true,
      image: [],
      comboPricing: {
        twoDoses: 0,
        threeDoses: 0
      }
    });
    
    this.productForm.markAsUntouched();
    this.productForm.markAsPristine();
    
    // Reset upload state completely
    this.resetUploadState();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.productForm.controls).forEach(key => {
      const control = this.productForm.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched();
      } else {
        control?.markAsTouched();
      }
    });
  }

  private arrayToString(array: any): string {
    // Handle different input types safely
    if (!array) return '';
    
    // If it's already a string, return as is
    if (typeof array === 'string') return array;
    
    // If it's an array, join it
    if (Array.isArray(array)) return array.join(', ');
    
    // If it's an object (like {0: "item1", 1: "item2"}), convert to array first
    if (typeof array === 'object' && array !== null) {
      const values = Object.values(array);
      // Only join if the values are strings
      if (values.every(val => typeof val === 'string')) {
        return values.join(', ');
      }
    }
    
    // If it's an object or other type, try to convert to string
    return String(array);
  }

  private parseArrayFromString(str: string): string[] {
    if (!str) return [];
    return str.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }

  // Utility methods
  getDoseTypeClass(doseType: DoseType): string {
    // Generate dynamic CSS class based on dose type
    if (!doseType) return 'dose-default';
    
    // Convert dose type to lowercase and create CSS class
    const normalizedType = doseType.toLowerCase().replace(/\s+/g, '-');
    return `dose-${normalizedType}`;
  }

  getDoseTypeText(doseType: DoseType): string {
    // Return the dose type as is, or format it nicely
    if (!doseType) return 'Unknown Dose';
    
    // Capitalize first letter of each word
    return doseType.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Get dose type class based on dose number for backward compatibility
   * @param doseNumber The dose number
   * @returns CSS class for the dose
   */
  getDoseNumberClass(doseNumber: number): string {
    if (!doseNumber) return 'dose-default';
    
    // Generate dynamic class based on dose number
    return `dose-number-${doseNumber}`;
  }

  /**
   * Get dose type text based on dose number for backward compatibility
   * @param doseNumber The dose number
   * @returns Formatted text for the dose
   */
  getDoseNumberText(doseNumber: number): string {
    if (!doseNumber) return 'Unknown Dose';
    
    return `Dose ${doseNumber}`;
  }

  /**
   * Get combined dose information (type + number)
   * @param product The product to get dose info for
   * @returns Combined dose information
   */
  getDoseInfo(product: Product): { type: string; number: string; class: string } {
    return {
      type: this.getDoseTypeText(product.doseType),
      number: this.getDoseNumberText(product.doseNumber),
      class: this.getDoseTypeClass(product.doseType)
    };
  }

  /**
   * Generate a unique color for any dose type
   * @param doseType The dose type string
   * @returns CSS custom properties for colors
   */
  getDoseTypeColors(doseType: DoseType): { primary: string; secondary: string } {
    if (!doseType) {
      return { primary: '#6c757d', secondary: '#495057' };
    }

    // Generate consistent colors based on dose type string
    const hash = this.hashString(doseType);
    const hue = hash % 360;
    const saturation = 60 + (hash % 20); // 60-80%
    const lightness = 45 + (hash % 15); // 45-60%

    const primary = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    const secondary = `hsl(${hue}, ${saturation}%, ${lightness - 10}%)`;

    return { primary, secondary };
  }

  /**
   * Simple hash function to generate consistent colors
   * @param str The string to hash
   * @returns Hash number
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Get all available dose types from existing products
   * @returns Array of unique dose types
   */
  getAvailableDoseTypes(): string[] {
    if (!this.products || this.products.length === 0) {
      return [];
    }
    
    // Get unique dose types and sort them
    const uniqueDoseTypes = [...new Set(this.products.map(p => p.doseType))];
    return uniqueDoseTypes.sort();
  }

  // Pagination methods
  get paginatedProducts(): Product[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredProducts.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Image handling
  getProductImage(product: Product): string {
    return product.image && product.image.length > 0 ? product.image[0] : '/product/product3Front.png';
  }

  // Status toggle
  async toggleProductStatus(product: Product): Promise<void> {
    try {
      await this.productService.toggleProductStatus(product.id, !product.isActive);
      this.toastService.success(`Product ${!product.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling product status:', error);
      this.toastService.error('Failed to update product status. Please try again.');
    }
  }

  // Stock toggle
  async toggleProductStock(product: Product): Promise<void> {
    try {
      await this.productService.toggleProductStock(product.id, !product.inStock);
      this.toastService.success(`Product stock ${!product.inStock ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error toggling product stock:', error);
      this.toastService.error('Failed to update product stock. Please try again.');
    }
  }

  // File upload methods
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.validateFile(file)) {
          this.selectedFiles.push(file);
          this.createImagePreview(file);
        }
      }
      // Trigger form validation update when files are selected
      this.productForm.updateValueAndValidity();
      // Also trigger validation on the image control specifically
      this.productForm.get('image')?.updateValueAndValidity();
    }
  }

  // Check Firebase Storage configuration
  private checkFirebaseStorage(): void {
    const status = this.fileUploadService.checkStorageStatus();
    console.log('Firebase Storage Status:', status);
    
    if (!status.initialized) {
      console.error('Firebase Storage not initialized:', status.error);
      this.toastService.error('Firebase Storage not initialized. Please check configuration.');
      return;
    }
    
    if (status.bucket !== 'height-clinic.firebasestorage.app') {
      console.warn('Storage bucket mismatch. Expected: height-clinic.firebasestorage.app, Got:', status.bucket);
      this.toastService.warning('Storage bucket configuration mismatch detected.');
    } else {
      console.log('Firebase Storage configured correctly:', status.bucket);
    }
  }

  // Check if public/product folder exists (for development)
  private checkProductFolder(): void {
    // In a real application, you would check if the folder exists on the server
    // For now, we'll just log that we're using the public/product folder
    console.log('Using upload folder: /public/product/');
  }

  private validateFile(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      this.toastService.error('Please select only image files (JPEG, PNG, WebP, SVG)');
      return false;
    }

    if (file.size > maxSize) {
      this.toastService.error('File size should be less than 5MB');
      return false;
    }

    return true;
  }

  private createImagePreview(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreviewUrls.push(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  removeSelectedImage(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.imagePreviewUrls.splice(index, 1);
    // Trigger form validation update
    this.productForm.updateValueAndValidity();
    // Also trigger validation on the image control specifically
    this.productForm.get('image')?.updateValueAndValidity();
  }

  removeExistingImage(index: number): void {
    const currentImages = this.productForm.get('image')?.value || [];
    
    // Validation: Check if this is the last image
    if (currentImages.length <= 1) {
      this.toastService.error('Cannot delete the last image. A product must have at least one image.');
      return;
    }
    
    // Get the image URL to delete from Firebase Storage
    const imageToDelete = currentImages[index];
    
    // Show confirmation dialog
    this.showImageDeleteConfirmation(imageToDelete, index);
  }


  private showImageDeleteConfirmation(imageUrl: string, index: number): void {
    const confirmed = confirm(
      'Are you sure you want to delete this image? This action cannot be undone.\n\n' +
      'The image will be permanently removed from both the database and Firebase Storage.'
    );
    
    if (confirmed) {
      this.deleteImageFromStorageAndUpdateForm(imageUrl, index);
    }
  }

  private async deleteImageFromStorageAndUpdateForm(imageUrl: string, index: number): Promise<void> {
    try {
      // Set loading state
      this.isDeletingImage = true;
      this.deletingImageIndex = index;
      this.cdr.detectChanges();
      
      // Delete from Firebase Storage if it's a Firebase Storage URL
      if (imageUrl && this.isFirebaseStorageUrl(imageUrl)) {
        await this.fileUploadService.deleteFile(imageUrl).toPromise();
      }
      
      // Remove from form array
      const currentImages = this.productForm.get('image')?.value || [];
      currentImages.splice(index, 1);
      this.productForm.patchValue({ image: currentImages });
      
      // If we're in edit mode, immediately update the database
      if (this.isEditing && this.editingProductId) {
        await this.updateProductInDatabase(currentImages);
      }
      
      // Trigger form validation update
      this.productForm.updateValueAndValidity();
      this.productForm.get('image')?.updateValueAndValidity();
      
      // Show success feedback
      this.toastService.success('Image removed successfully from the product.');
      
    } catch (error) {
      console.error('Error deleting image:', error);
      this.toastService.error('Failed to delete image. Please try again.');
    } finally {
      // Clear loading state
      this.isDeletingImage = false;
      this.deletingImageIndex = null;
      this.cdr.detectChanges();
    }
  }

  private async updateProductInDatabase(updatedImages: string[]): Promise<void> {
    try {
      if (!this.editingProductId) {
        throw new Error('No product ID available for update');
      }

      const productData: Partial<Product> = {
        image: updatedImages
      };

      await this.productService.updateProduct(this.editingProductId, productData);
      console.log('Product updated in database with new image array');
      
    } catch (error) {
      console.error('Error updating product in database:', error);
      this.toastService.error('Failed to update product in database. Please try again.');
      throw error;
    }
  }

  private isFirebaseStorageUrl(url: string): boolean {
    return url.includes('firebasestorage.googleapis.com') || url.startsWith('product-images/');
  }

  getDeleteButtonStyle(index: number): string {
    const baseStyle = 'width: 100% !important; padding: 0.5rem 1rem !important;';
    const isDisabled = this.isDeletingImage || (this.productForm.get('image')?.value?.length <= 1);
    
    if (isDisabled) {
      return `${baseStyle} background-color: #6c757d !important; border-color: #6c757d !important; color: white !important;`;
    }
    
    return baseStyle;
  }

  // Clear all existing images (useful when replacing all images)
  clearAllExistingImages(): void {
    const confirmed = confirm(
      'Are you sure you want to clear all existing images? This action cannot be undone.\n\n' +
      'All images will be permanently removed from both the database and Firebase Storage.'
    );
    
    if (confirmed) {
      this.clearAllImagesWithConfirmation();
    }
  }

  private async clearAllImagesWithConfirmation(): Promise<void> {
    try {
      this.isClearingAllImages = true;
      this.cdr.detectChanges();
      
      this.toastService.info('Clearing all images...');
      
      const currentImages = this.productForm.get('image')?.value || [];
      
      // Delete all images from Firebase Storage
      if (currentImages.length > 0) {
        const deletePromises = currentImages
          .filter((imageUrl: string) => this.isFirebaseStorageUrl(imageUrl))
          .map((imageUrl: string) => this.fileUploadService.deleteFile(imageUrl).toPromise());
        
        await Promise.all(deletePromises);
        this.toastService.success('All images deleted from Firebase Storage successfully!');
      }
      
      // Clear form and selected files
      this.productForm.patchValue({ image: [] });
      this.selectedFiles = [];
      this.imagePreviewUrls = [];
      
      // If we're in edit mode, update the database
      if (this.isEditing && this.editingProductId) {
        await this.updateProductInDatabase([]);
      }
      
      // Trigger form validation update
      this.productForm.updateValueAndValidity();
      this.productForm.get('image')?.updateValueAndValidity();
      
      this.toastService.success('All existing images cleared. Ready for new uploads.');
      
    } catch (error) {
      console.error('Error clearing all images:', error);
      this.toastService.error('Failed to clear all images. Please try again.');
    } finally {
      this.isClearingAllImages = false;
      this.cdr.detectChanges();
    }
  }

  getDefaultImageUrl(productName: string, doseType: string): string {
    // Legacy color mapping for backward compatibility
    const legacyColors = {
      green: '66BB6A',
      yellow: 'FFB74D',
      red: 'D32F2F'
    };
    
    let color = '66BB6A'; // Default color
    
    // Check if it's a legacy dose type
    if (legacyColors[doseType as keyof typeof legacyColors]) {
      color = legacyColors[doseType as keyof typeof legacyColors];
    } else if (doseType) {
      // Generate a consistent color for new dose types
      const hash = this.hashString(doseType);
      const hue = hash % 360;
      const saturation = 60 + (hash % 20); // 60-80%
      const lightness = 45 + (hash % 15); // 45-60%
      
      // Convert HSL to hex (simplified)
      color = this.hslToHex(hue, saturation, lightness);
    }
    
    const text = encodeURIComponent(productName || 'Dose One');
    return `https://placehold.co/400x400/${color}/31343C?font=poppins&text=${text}`;
  }

  /**
   * Convert HSL to hex color
   * @param h Hue (0-360)
   * @param s Saturation (0-100)
   * @param l Lightness (0-100)
   * @returns Hex color string
   */
  private hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `${f(0)}${f(8)}${f(4)}`;
  }

  // Get the first image from product handling both array and object formats
  getFirstImage(product: Product): string {
    if (!product?.image) return '';
    
    // Handle both array and object formats
    const imageArray = Array.isArray(product.image) 
      ? product.image 
      : Object.values(product.image);
    
    const firstImage = imageArray[0];
    return (typeof firstImage === 'string') ? firstImage : '';
  }

  // Get image array handling both array and object formats
  getImageArray(imageData: any): string[] {
    if (!imageData) return [];
    
    // Handle both array and object formats
    const imageArray = Array.isArray(imageData) 
      ? imageData 
      : Object.values(imageData);
    
    // Filter out non-string values and ensure type safety
    const filteredArray = imageArray.filter((img): img is string => typeof img === 'string');
    
    // Return empty array if no valid images
    return filteredArray;
  }

  getImageUrl(imagePath: string, productName: string, doseType: string): string {
    if (imagePath && imagePath !== '') {
      console.log('Admin getImageUrl called with:', imagePath);
      
      // Check if it's a Firebase Storage URL
      if (imagePath.startsWith('https://firebasestorage.googleapis.com/')) {
        console.log('Using Firebase Storage URL directly');
        return imagePath;
      }
      // Check if it's a full URL or data URL
      if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
        console.log('Using full URL directly');
        return imagePath;
      }
      // For Firebase Storage paths (product-images/filename)
      if (imagePath.startsWith('product-images/')) {
        console.log('Converting Firebase Storage path to URL');
        // For Firebase Storage paths, we need to construct the full URL
        // This is a simplified approach - in production you'd use Firebase Storage SDK
        return imagePath; // Return as-is for now, frontend will handle it
      }
      // For legacy relative paths
      if (imagePath.startsWith('/public/product/')) {
        console.log('Using legacy public path');
        return imagePath;
      }
      // If it's just a filename, assume it's in Firebase Storage
      if (!imagePath.includes('/')) {
        console.log('Converting filename to Firebase Storage path');
        return `product-images/${imagePath}`;
      }
      console.log('Using path as-is');
      return imagePath;
    }
    console.log('No image path, not using default');
    return '';
  }

  // Custom validator for image array
  private imageArrayValidator() {
    return (control: any) => {
      const value = control.value;
      
      // If there are selected files, validation passes
      if (this.selectedFiles && this.selectedFiles.length > 0) {
        return null;
      }
      
      // If there are existing images, validation passes
      if (value && Array.isArray(value) && value.length > 0) {
        return null;
      }
      
      // If neither selected files nor existing images, validation fails
      return { noImages: true };
    };
  }

  // Check if form has valid images (either existing or newly selected)
  hasValidImages(): boolean {
    const existingImages = this.productForm.get('image')?.value || [];
    const hasExistingImages = existingImages.length > 0;
    const hasSelectedFiles = this.selectedFiles.length > 0;
    
    return hasExistingImages || hasSelectedFiles;
  }

  // Multilingual modal methods
  multilingualModalRef : any
  openMultilingualModal(modal: any): void {
    this.populateMultilingualForm();
    this.multilingualModalRef = this.modalService.open(modal, { size: 'lg' });
  }

  private populateMultilingualForm(): void {
    if (this.isEditing && this.selectedProduct) {
      const i18n = this.selectedProduct.i18n || {};
      
      // Populate Hindi fields
      this.multilingualForm.patchValue({
        hi_name: i18n['hi']?.name || '',
        hi_description: i18n['hi']?.description || '',
        hi_features: this.arrayToString(i18n['hi']?.features || []),
        hi_benefits: this.arrayToString(i18n['hi']?.benefits || []),
        hi_usage: this.arrayToString(i18n['hi']?.usage || []),
        // Populate Marathi fields
        mr_name: i18n['mr']?.name || '',
        mr_description: i18n['mr']?.description || '',
        mr_features: this.arrayToString(i18n['mr']?.features || []),
        mr_benefits: this.arrayToString(i18n['mr']?.benefits || []),
        mr_usage: this.arrayToString(i18n['mr']?.usage || []),
        // Populate Kannada fields
        kn_name: i18n['kn']?.name || '',
        kn_description: i18n['kn']?.description || '',
        kn_features: this.arrayToString(i18n['kn']?.features || []),
        kn_benefits: this.arrayToString(i18n['kn']?.benefits || []),
        kn_usage: this.arrayToString(i18n['kn']?.usage || [])
      });
    } else {
      // Reset form for new products
      this.multilingualForm.reset();
    }
  }

  saveMultilingualContent(): void {
    const formValue = this.multilingualForm.value;
    
    // Build i18n object
    const i18n: any = {};
    
    // Add Hindi translations if any field has content
    if (formValue.hi_name || formValue.hi_description || formValue.hi_features || formValue.hi_benefits || formValue.hi_usage) {
      i18n['hi'] = {
        name: formValue.hi_name || undefined,
        description: formValue.hi_description || undefined,
        features: formValue.hi_features ? this.stringToArray(formValue.hi_features) : undefined,
        benefits: formValue.hi_benefits ? this.stringToArray(formValue.hi_benefits) : undefined,
        usage: formValue.hi_usage ? this.stringToArray(formValue.hi_usage) : undefined
      };
    }
    
    // Add Marathi translations if any field has content
    if (formValue.mr_name || formValue.mr_description || formValue.mr_features || formValue.mr_benefits || formValue.mr_usage) {
      i18n['mr'] = {
        name: formValue.mr_name || undefined,
        description: formValue.mr_description || undefined,
        features: formValue.mr_features ? this.stringToArray(formValue.mr_features) : undefined,
        benefits: formValue.mr_benefits ? this.stringToArray(formValue.mr_benefits) : undefined,
        usage: formValue.mr_usage ? this.stringToArray(formValue.mr_usage) : undefined
      };
    }
    
    // Add Kannada translations if any field has content
    if (formValue.kn_name || formValue.kn_description || formValue.kn_features || formValue.kn_benefits || formValue.kn_usage) {
      i18n['kn'] = {
        name: formValue.kn_name || undefined,
        description: formValue.kn_description || undefined,
        features: formValue.kn_features ? this.stringToArray(formValue.kn_features) : undefined,
        benefits: formValue.kn_benefits ? this.stringToArray(formValue.kn_benefits) : undefined,
        usage: formValue.kn_usage ? this.stringToArray(formValue.kn_usage) : undefined
      };
    }
    
    // Update the product form with i18n data
    this.productForm.patchValue({ i18n: Object.keys(i18n).length > 0 ? i18n : null });
    
    this.toastService.success('Multilingual content saved successfully!');
    this.multilingualModalRef.dismiss();
  }

  private stringToArray(str: any): string[] {
    // Handle different input types safely
    if (!str) return [];
    
    // If it's already an array, return as is
    if (Array.isArray(str)) return str;
    
    // Convert to string and process
    const stringValue = String(str).trim();
    if (stringValue === '') return [];
    
    return stringValue.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }

}
